import React, { useMemo } from "react";
import { Alert, AlertTitle, Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate } from "react-router-dom";

import { ringFromParcel, ringAreaM2, describeArea } from "../../utils/parcelGeometry";
import { describe as describeDegradation } from "../../utils/degradationCurve";
import { mountingPlaneFor } from "../../utils/mounting";
import { useIsGreek } from "../../context/LanguageContext";
import {
  bearing as compassPoint,
  displayName,
  orientationsFromConfig,
  orientationSummary,
} from "../../components/config/orientations";

/**
 * The PV park and its connection, read at a glance.
 *
 * Everything on the technical page used to be about equipment: which module,
 * which inverter, how they string. Nothing said where the plant stands, how the
 * rows are laid, or what it connects to -- those live on three other pages and
 * a reviewer had to visit all of them. This card reads them back, read-only,
 * in the order a study describes a plant: site, orientation, rows, strings,
 * modelling, connection. Each group names the page that owns the value, so a
 * correction goes to the right place instead of tempting a second copy here.
 *
 * Three things were missing and are now here, because a summary that only
 * repeats numbers the reader has already typed earns nothing:
 *
 *  - a HEADLINE row. Thirty values at one weight is a table, not a summary:
 *    four of them decide the study and the rest are there to be checked.
 *  - CONSISTENCY CHECKS. "Ground coverage 0.456" leaves the judgement to the
 *    reader; "the tables do not fit the parcel" is what the reader came for.
 *    Only failing checks are drawn, so a clean design shows a clean card.
 *  - the BESS and the transformer, in the same visual language as everything
 *    else. They used to sit above in a third and fourth shape of their own.
 *
 * Nothing here is editable, degradation included. It used to be: a single
 * "%/year" box among the site inputs, standing in for a whole warranty curve.
 * It is now the module's degradation curve, set beside the module it belongs
 * to on the PV System page -- a module fact edited on a financial page is a
 * module fact nobody thinks to check against a datasheet.
 */

const num = (v) => {
  const n = Number(v);
  return v === null || v === undefined || v === "" || !Number.isFinite(n) ? null : n;
};
const fmt0 = (v, unit = "") => (num(v) === null ? "—" : `${Math.round(num(v)).toLocaleString("en-GB")}${unit}`);
// Capacities in the reader's locale, with the unit that keeps the digits
// honest: "1.020 MW" reads as one thousand and twenty to a Greek eye, so a
// plant under 10 MW is stated in whole kW and the grouping does the rest.
const power = (kw, unit = "W") => {
  const n = num(kw);
  if (n === null) return "—";
  if (Math.abs(n) < 10000) return `${Math.round(n).toLocaleString("en-GB")} k${unit}`;
  return `${(n / 1000).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M${unit}`;
};

const MOUNT = { fixed: "Fixed tilt", single_axis: "Single-axis tracker", dual_axis: "Dual-axis tracker" };
const SHADING = { bypass: "Strings with bypass diodes", linear: "Linear (irradiance only)", module: "Whole module", none: "Not modelled" };

function bearing(azimuth, lat) {
  const a = num(azimuth);
  if (a === null) return "";
  const facing = lat !== null && lat < 0 ? 0 : 180;
  const d = ((a - facing + 180) % 360 + 360) % 360 - 180;
  if (Math.abs(d) < 0.05) return facing === 180 ? "due south" : "due north";
  return `${Math.abs(d).toFixed(1)}° ${d > 0 ? "west" : "east"} of ${facing === 180 ? "south" : "north"}`;
}

function stringsSummary(systemDesign) {
  const inverters = Object.values(systemDesign || {});
  let strings = 0, modules = 0, min = Infinity, max = 0;
  inverters.forEach((mppts) =>
    (mppts || []).forEach((m) =>
      (m?.inputs || []).forEach((i) => {
        const n = num(i?.modules_per_string);
        if (n && n > 0) { strings += 1; modules += n; min = Math.min(min, n); max = Math.max(max, n); }
      })
    )
  );
  return { inverters: inverters.length, strings, modules, min: strings ? min : null, max: strings ? max : null };
}

/**
 * One headline number. Same shape for every one of them, whatever it measures:
 * a small icon on a tinted disc, a thin line of the tile's own colour, the
 * label, the figure, one line of context. Each tile has a hue of its own so
 * the row reads at a glance; the figure itself stays in text ink. A tile
 * with nothing in it ("—") steps back so the ones with a figure lead; a tile
 * with something to flag turns amber and says so with a mark beside the
 * hint, not with colour alone.
 */
const TILE_HUES = {
  pv: "#15803d",          // green: the plant
  storage: "#6d28d9",     // violet: the battery
  dcac: "#2563eb",        // blue: the inverter ratio
  export: "#0f766e",      // teal: the grid connection
  transformer: "#475569", // slate: the transformer
};

function Kpi({ label, value, hint, tone = "default", icon: Icon = null, hue = "dcac" }) {
  const empty = value === "none" || value === "—";
  const shown = value === "none" ? "—" : value;
  const color = tone === "warn" ? "warning.main" : tone === "bad" ? "error.main" : empty ? "text.secondary" : "text.primary";
  const statusHue = (theme) => (tone === "warn" ? theme.palette.warning.main : tone === "bad" ? theme.palette.error.main : null);
  const tileHue = (theme) => statusHue(theme) || (empty ? theme.palette.text.disabled : (TILE_HUES[hue] || theme.palette.primary.main));
  return (
    <Box
      sx={(theme) => ({
        position: "relative",
        overflow: "hidden",
        px: 1.75,
        py: 1.5,
        pr: Icon ? 6 : 1.75,
        minWidth: 0,
        height: "100%",
        boxSizing: "border-box",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(tileHue(theme), empty ? 0.18 : 0.28),
        background: `linear-gradient(180deg, ${alpha(tileHue(theme), empty ? 0.03 : 0.07)} 0%, ${alpha(tileHue(theme), 0.01)} 100%)`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        // The tile's colour, as a line along the top.
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0, right: 0, top: 0,
          height: 3,
          backgroundColor: alpha(tileHue(theme), empty ? 0.35 : 1),
        },
      })}
    >
      {Icon ? (
        <Box
          aria-hidden
          sx={(theme) => ({
            position: "absolute",
            top: 14,
            right: 12,
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tileHue(theme),
            backgroundColor: alpha(tileHue(theme), empty ? 0.08 : 0.14),
          })}
        >
          <Icon sx={{ fontSize: 19 }} />
        </Box>
      ) : null}
      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.7, fontSize: 10.5, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: empty ? 600 : 800, fontSize: empty ? 20 : 22, lineHeight: 1.2, color, overflowWrap: "anywhere" }}>
        {shown}
      </Typography>
      {hint ? (
        <Typography variant="caption" sx={{ color: tone === "default" ? "text.secondary" : color, display: "flex", alignItems: "center", gap: 0.4, lineHeight: 1.3, mt: 0.35, fontWeight: tone === "default" ? 400 : 600 }}>
          {tone !== "default" ? <WarningAmberRoundedIcon sx={{ fontSize: 13 }} /> : null}
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function Group({ title, owner, rows }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary" }}>
          {title}
        </Typography>
        {owner ? <Typography variant="caption" sx={{ color: "text.disabled" }}>· {owner}</Typography> : null}
      </Stack>
      <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.35 }}>
        {rows.filter(Boolean).map(([k, v]) => (
          <React.Fragment key={k}>
            <Typography component="dt" variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{k}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{v}</Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/**
 * What the design says about itself that the reader would otherwise have to
 * work out. Every check is computed from stored values only; a check whose
 * inputs are missing is skipped rather than guessed, so an empty list means
 * "nothing detected", never "all clear".
 */
/** Module positions a saved layout offers: columns per row times the stack. */
function layoutPositionsOf(spacing) {
  const columns = Array.isArray(spacing?.row_modules)
    ? spacing.row_modules.reduce((a, r) => a + (num(r) || 0), 0)
    : null;
  return columns ? columns * (num(spacing.row_vertical_modules) || 1) : null;
}

function consistencyChecks({ cfg, spacing, strings, parcelArea, transformerRatedKva, tracker, planes = null, isGreek = false }) {
  const out = [];
  const grid = cfg?.grid || {};
  const pvKw = num(cfg?.pvInstalledCap?.kw);
  const exportKw = num(grid.exportLimitKw);
  const landM2 = num(spacing.terrace_area_m2 ?? spacing.terrace_area);
  const gcr = num(spacing.gcr);

  // Each check is a fixed sentence plus the numbers behind it. The DOM-level
  // Greek bridge matches whole text nodes, so a sentence with a figure baked
  // into it can never be translated; keeping the wording static and the
  // figures in their own node is what makes these readable in both languages.
  if (parcelArea && landM2 && landM2 > parcelArea * 1.02) {
    out.push({
      severity: "error",
      title: "The layout does not fit the parcel drawn on the Project page.",
      detail: [[`${Math.round(landM2).toLocaleString("en-GB")} m²`, "rows"], [`${Math.round(parcelArea).toLocaleString("en-GB")} m²`, "parcel"]],
    });
  }
  // Fixed racking in southern Europe normally lands between 0.35 and 0.50;
  // a tracker at the same figure is already self-shading. One threshold for
  // both was what made a perfectly ordinary fixed-tilt field raise a flag.
  // Flush on a roof the "rows" are lines of a grid lying on the roof plane:
  // a coverage above 1 is the module meeting the next one, not self-shading.
  const flushRoof = cfg?.panelOrientation?.installationType === "roof_flush";
  if (!flushRoof && gcr !== null && gcr > (tracker ? 0.45 : 0.55)) {
    out.push({
      severity: "warning",
      title: "Ground coverage is high; row shading will take a visible share of the yield.",
      detail: [[`GCR ${gcr.toFixed(3)}`, ""]],
    });
  }
  if (pvKw && exportKw && exportKw < pvKw * 0.8) {
    out.push({
      severity: "info",
      title: "Export is limited well below the installed DC, so clipping at the connection point is expected.",
      detail: [[power(exportKw), "export"], [power(pvKw, "Wp"), "installed"]],
    });
  }
  if (transformerRatedKva && exportKw && transformerRatedKva < exportKw * 0.95) {
    out.push({
      severity: "warning",
      title: "The transformer is rated below the export limit. Either the rating or the limit is wrong.",
      detail: [[`${Math.round(transformerRatedKva).toLocaleString("en-GB")} kVA`, "transformer"], [power(exportKw), "export"]],
    });
  }
  // The electrical design and the physical layout are edited on two tabs and
  // saved separately. `row_modules` holds the module COLUMNS of each row, so
  // the positions on the ground are those columns times the modules stacked
  // per row -- reading the columns as modules made a field with room to spare
  // look short by the stacking factor.
  const layoutColumns = Array.isArray(spacing.row_modules)
    ? spacing.row_modules.reduce((a, r) => a + (num(r) || 0), 0)
    : null;
  const layoutPositions = layoutColumns
    ? layoutColumns * (num(spacing.row_vertical_modules) || 1)
    : null;
  // Spare positions are normal: a string takes whole columns, so the last
  // column of each row is usually part-filled. Too FEW is the fault, because
  // the run follows the electrical design and the drawing stops matching it.
  if (planes && planes.length > 1) {
    // Several planes: each plane's rows hold the modules attached to THAT
    // plane, so each is checked against its own - the plant's total against
    // the first plane's rows would flag every multi-plane design.
    planes.forEach((plane) => {
      const positions = layoutPositionsOf(plane.spacing);
      if (positions && plane.modules && positions < plane.modules) {
        out.push({
          severity: "error",
          title: "The rows do not have room for every module in the electrical design. The run follows the electrical design.",
          detail: [[displayName(plane.orientation, isGreek), ""], [plane.modules.toLocaleString("en-GB"), "in the design"], [positions.toLocaleString("en-GB"), "positions on the ground"]],
        });
      }
    });
  } else if (layoutPositions && strings.modules && layoutPositions < strings.modules) {
    out.push({
      severity: "error",
      title: "The rows do not have room for every module in the electrical design. The run follows the electrical design.",
      detail: [[strings.modules.toLocaleString("en-GB"), "in the design"], [layoutPositions.toLocaleString("en-GB"), "positions on the ground"]],
    });
  }
  if (!describeDegradation(cfg?.pvDegradation?.retentionPct) && num(cfg?.annualDegFactor) === null) {
    out.push({
      severity: "warning",
      title: "No degradation curve is set for the module, so the study holds the output flat over its whole life.",
      detail: null,
    });
  }
  return out;
}

/**
 * `transformerKva` is the text for the Transformer row, prepared by the page:
 * the stored `transformerDets.rated` is only the answer when the rating is set
 * by hand; with "Recommended Rating" on it is a leftover and the run sizes the
 * unit itself, which the page can estimate from the inverter details it holds.
 *
 * `systemTypeLabel`, `bessRows` and `dcAcRatio` are optional: the page owns
 * them and passes them so this card can state the whole system in one place
 * instead of leaving a second, differently-shaped summary above it.
 */
// onDegradationChange is gone with the field it wrote: the curve is edited on
// the PV System page, beside the module it describes.
export default function PvParkSummary({ cfg, transformerKva, systemTypeLabel, bessRows, dcAcRatio }) {
  const isGreek = useIsGreek();
  const fmt = (v, d = 1, unit = "") => (num(v) === null ? "—" : `${num(v).toLocaleString("en-GB", { maximumFractionDigits: d, minimumFractionDigits: d })}${unit}`);
  // Battery power / energy in the unit that fits its size: MW / MWh from 1 up,
  // kW / kWh below that, so a 15 kWh battery reads 15.36 kWh, not 0.02 MWh.
  const storageSize = (valueM, unitM, unitK) => {
    const v = num(valueM);
    if (v === null) return "—";
    if (Math.abs(v) >= 1) return fmt(v, 2, ` ${unitM}`);
    const k = v * 1000;
    return `${k.toLocaleString("en-GB", { maximumFractionDigits: Math.abs(k) < 100 ? 2 : 0 })} ${unitK}`;
  };
  const navigate = useNavigate();
  const loc = cfg?.loc_info || {};
  const po = cfg?.panelOrientation || {};
  // Memoised because the checks below depend on it: a fresh {} on every render
  // would re-run them on every keystroke elsewhere on the page.
  const spacing = useMemo(
    () => cfg?.pv_design?.spacingInfo || cfg?.pv_design?.spacing || {},
    [cfg?.pv_design?.spacingInfo, cfg?.pv_design?.spacing]
  );
  const grid = cfg?.grid || {};
  const lat = num(loc.lat);

  const parcelRing = ringFromParcel(loc.parcel);
  const parcelArea = parcelRing ? ringAreaM2(parcelRing) : null;
  const mount = String(cfg?.mountType || "fixed").toLowerCase();
  const tracker = mount !== "fixed";
  const plane = mountingPlaneFor(cfg);
  const strings = useMemo(() => stringsSummary(cfg?.systemDesign), [cfg?.systemDesign]);
  const rowsList = Array.isArray(spacing.row_modules) && spacing.row_modules.length ? spacing.row_modules : null;
  const rowCount = rowsList ? rowsList.length : num(spacing.field_rows);
  const overrides = spacing.string_overrides ? Object.keys(spacing.string_overrides).length : 0;
  const horizonPoints = loc?.horizonProfile || po?.horizonProfile;
  const horizonCount = horizonPoints && typeof horizonPoints === "object" ? Object.keys(horizonPoints).length : 0;
  const shading = String(po.nearShadingModel || "bypass").toLowerCase();
  const soiling = num(cfg?.pvLosses?.dc?.soiling);
  // The losses page stores the twelve values under dc.soilingMonthly; the
  // summary looked for an older key and printed the annual field the engine
  // does not use while a profile is set.
  const soilingProfile = [cfg?.pvLosses?.dc?.soilingMonthly, cfg?.pvLosses?.soiling_monthly]
    .find((list) => Array.isArray(list) && list.length === 12 && list.every((v) => num(v) !== null)) || null;
  const soilingMonthly = Boolean(soilingProfile);
  const flushRoof = po.installationType === "roof_flush";
  const onRoof = String(po.installationType || "").startsWith("roof");
  const albedoMonthly = Array.isArray(loc?.albedo_monthly) && loc.albedo_monthly.length === 12;

  // Read out of the stored table, not out of any pair of headline numbers:
  // the table is what the engine uses.
  const degradationCurve = describeDegradation(cfg?.pvDegradation?.retentionPct);
  const degradation = degradationCurve
    ? `${degradationCurve.uniform
          ? `${degradationCurve.firstYearPct.toFixed(2)}% first year, then ${degradationCurve.annualPct.toFixed(2)}% / yr`
          : "entered year by year"} · ${degradationCurve.finalRetentionPct.toFixed(1)}% at year ${degradationCurve.years}`
    : num(cfg?.annualDegFactor) === null
      ? "—"
      // No curve on this project: the old single field, named as the old
      // single field rather than dressed up as a curve it never was.
      : `${fmt(cfg.annualDegFactor, 2, " % / year")} (single rate, no curve set)`;

  const bess = useMemo(() => {
    const rows = Array.isArray(bessRows) ? bessRows : [];
    // powerMw / energyMwh are per unit, as the battery table states them;
    // the plant total is the sum over the counts.
    return rows.reduce(
      (acc, r) => {
        const count = num(r?.count) ?? 1;
        acc.powerMw += (num(r?.powerMw) ?? 0) * count;
        acc.energyMwh += (num(r?.energyMwh) ?? 0) * count;
        acc.units += count;
        return acc;
      },
      { powerMw: 0, energyMwh: 0, units: 0 }
    );
  }, [bessRows]);

  // A plant on several planes: each plane with its effective angles, the
  // modules attached to it and its own saved rows. Null on a single plane,
  // which keeps the card exactly as it was.
  const tr = (el, en) => (isGreek ? el : en);
  const planes = useMemo(() => {
    const list = orientationsFromConfig(cfg);
    if (list.length < 2) return null;
    const modules = Object.fromEntries(orientationSummary(cfg).map((s) => [s.id, s.modules]));
    return list.map((o, i) => ({
      orientation: o,
      index: i + 1,
      plane: mountingPlaneFor({
        ...cfg,
        panelOrientation: { ...(cfg?.panelOrientation || {}), tilt: o.tilt, azim: o.azim, tiltReference: o.tiltReference },
      }),
      modules: modules[o.id] || 0,
      spacing: i === 0 ? spacing : (o.spacingInfo || null),
    }));
  }, [cfg, spacing]);

  const transformerRatedKva = cfg?.useRecommendedTransformer ? null : num(cfg?.transformerDets?.rated);
  const checks = useMemo(
    () => consistencyChecks({ cfg, spacing, strings, parcelArea, transformerRatedKva, tracker, planes, isGreek }),
    [cfg, spacing, strings, parcelArea, transformerRatedKva, tracker, planes, isGreek]
  );

  const pvKw = num(cfg?.pvInstalledCap?.kw);
  const exportKw = num(grid.exportLimitKw);
  const gcr = num(spacing.gcr);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
            System summary
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            What will be simulated: the plant, where it stands, how its rows and strings are laid, and what it connects to.
            Read from the pages that own each value; corrections go to the page named in each group.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={() => navigate("/project/asset?tab=pv&design=sld")} sx={{ whiteSpace: "nowrap" }}>
          Single-line diagram
        </Button>
      </Stack>

      {/* The four or five numbers that decide the study. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
          alignItems: "stretch",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Kpi label="PV capacity" value={power(pvKw, "Wp")} hint={systemTypeLabel || undefined} icon={SolarPowerIcon} hue="pv" />
        <Kpi
          label="Storage"
          value={bess.units ? `${storageSize(bess.powerMw, "MW", "kW")} / ${storageSize(bess.energyMwh, "MWh", "kWh")}` : "none"}
          hint={bess.units ? `${bess.units} × BESS` : undefined}
          icon={BatteryChargingFullIcon}
          hue="storage"
        />
        <Kpi
          label="DC / AC"
          value={dcAcRatio ? fmt(dcAcRatio, 2) : "—"}
          hint={dcAcRatio ? (dcAcRatio > 1.35 ? "clipping likely" : "within the usual range") : undefined}
          tone={dcAcRatio && dcAcRatio > 1.5 ? "warn" : "default"}
          icon={SwapVertIcon}
          hue="dcac"
        />
        <Kpi
          label="Export limit"
          value={power(exportKw)}
          hint={grid.enforceLimits ? "enforced 24/7" : undefined}
          tone={pvKw && exportKw && exportKw < pvKw * 0.8 ? "warn" : "default"}
          icon={ElectricalServicesIcon}
          hue="export"
        />
        <Kpi
          label="Transformer"
          value={!cfg?.hasTransformer ? "none" : (transformerKva || "sized by the run")}
          hint={cfg?.hasTransformer && cfg?.useRecommendedTransformer ? "recommended rating" : undefined}
          icon={SettingsInputComponentIcon}
          hue="transformer"
        />
      </Box>

      {checks.length > 0 && (
        <Alert severity={checks.some((c) => c.severity === "error") ? "error" : checks.some((c) => c.severity === "warning") ? "warning" : "info"} sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>Worth checking before the run</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.4 }}>
            {checks.map((c) => (
              <Typography key={c.title} component="li" variant="body2">
                {c.title}
                {c.detail?.length ? (
                  <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                    {" ("}
                    {c.detail.map(([value, word], i) => (
                      <React.Fragment key={value}>
                        {i ? " · " : ""}
                        {value}
                        {word ? <> <span>{word}</span></> : null}
                      </React.Fragment>
                    ))}
                    {")"}
                  </Typography>
                ) : null}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2.5 }}>
        <Group
          title="Site"
          owner="Project"
          rows={[
            ["Coordinates", lat === null ? "—" : `${Math.abs(lat).toFixed(4)}° ${lat >= 0 ? "N" : "S"}, ${Math.abs(num(loc.lon) || 0).toFixed(4)}° ${num(loc.lon) >= 0 ? "E" : "W"}`],
            ["Altitude", fmt0(loc.elevation_m, " m")],
            ["Parcel", parcelArea ? describeArea(parcelArea) : "not drawn"],
            ["Ground slope", num(loc.tilt) ? `${fmt(loc.tilt)}° towards ${fmt0(loc.azimuth)}°` : "level"],
            ["Surface", cfg?.surfaceType ? `${cfg.surfaceType}${albedoMonthly ? " · albedo by month" : ""}` : "—"],
          ]}
        />
        <Group
          title="Orientation"
          owner="PV System"
          rows={[
            ["Mounting", MOUNT[mount] || mount],
            // Several planes: one line each, in place of the single tilt and azimuth.
            ...(planes ? planes.map((p) => [
              tr(`Προσανατολισμός ${p.index}`, `Orientation ${p.index}`),
              `${!p.orientation.autoName && p.orientation.name ? `${p.orientation.name} · ` : ""}`
                + `${fmt(p.plane.tilt, 1, "°")} / ${fmt(p.plane.azimuth, 1, "°")}`
                + `${compassPoint(p.plane.azimuth, isGreek) ? ` (${compassPoint(p.plane.azimuth, isGreek)})` : ""}`
                + ` · ${fmt0(p.modules)} ${tr("πλαίσια", "modules")}`,
            ]) : []),
            !tracker && !planes && ["Tilt", plane.changed
              ? `${fmt(plane.tilt, 1, "°")} from the horizontal (${fmt(po.tilt, 1, "°")} on racking that follows the ground)`
              : fmt(po.tilt, 1, "°")],
            !planes && ["Azimuth", num(po.azim) === null ? "—" : plane.changed
              ? `${fmt(plane.azimuth, 1, "°")} (${bearing(plane.azimuth, lat)}; racking set to ${fmt(po.azim, 1, "°")})`
              : `${fmt(po.azim, 1, "°")} (${bearing(po.azim, lat)})`],
            tracker && ["Axis", `${cfg?.axisOrientation || "—"}, ±${fmt0(cfg?.maxAngle, "°")}${cfg?.backTrack ? ", backtracking" : ""}`],
            onRoof ? [tr("Ύψος από τη στέγη", "Clearance above roof"), fmt(po.groundClearanceM, 2, " m")]
              : ["Ground clearance", fmt(po.groundClearanceM, 2, " m")],
            ["Module orientation", po.moduleOrientation === "landscape" ? "Landscape" : "Portrait"],
          ]}
        />
        <Group
          title="Rows"
          owner="PV System · Physical layout"
          rows={[
            planes && [tr("Προσανατολισμός", "Orientation"), displayName(planes[0].orientation, isGreek)],
            ["Rows", !rowCount ? "—" : !rowsList ? fmt0(rowCount) : (
              <>{fmt0(rowCount)} <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: "text.secondary" }}>
                {spacing.parcel_tables ? "tables, from the parcel" : "set row by row"}
              </Typography></>
            )],
            ["Stacked per row", fmt0(spacing.row_vertical_modules)],
            ["Pitch", fmt(spacing.minimum_distance_m ?? spacing.pitch_m ?? spacing.pitch, 2, " m")],
            flushRoof ? null : ["Ground coverage", gcr === null ? "—" : fmt(gcr, 3)],
            flushRoof ? null : ["Land occupied", fmt0(spacing.terrace_area_m2 ?? spacing.terrace_area, " m²")],
            spacing.setback_m ? ["Setback", fmt(spacing.setback_m, 1, " m")] : null,
            // The other planes' rows, one line each: pitch and ground coverage,
            // or that the plane has not been laid out yet.
            ...(planes ? planes.slice(1).map((p) => {
              const s = p.spacing || {};
              const pitch = num(s.minimum_distance_m ?? s.pitch_m ?? s.pitch);
              return [
                tr(`Προσανατολισμός ${p.index}`, `Orientation ${p.index}`),
                !p.spacing ? tr("χωρίς διάταξη ακόμη", "not laid out yet")
                  : `${tr("βήμα", "pitch")} ${fmt(pitch, 2, " m")}${num(s.gcr) === null ? "" : ` · GCR ${fmt(s.gcr, 3)}`}`,
              ];
            }) : []),
          ]}
        />
        <Group
          title="Strings"
          owner="PV System · Electrical design"
          rows={[
            ["Inverters", fmt0(strings.inverters)],
            ["Strings", fmt0(strings.strings)],
            ["Modules per string", strings.min === null ? "—" : strings.min === strings.max ? fmt0(strings.min) : `${strings.min}–${strings.max}`],
            ["Modules in strings", fmt0(strings.modules)],
            overrides ? ["Placed by hand", fmt0(overrides)] : null,
          ]}
        />
        <Group
          title="Modelling"
          owner="PV System · Modelling"
          rows={[
            flushRoof ? [tr("Σκίαση σειρών", "Row shading"), tr("δεν εφαρμόζεται — συνεπίπεδα πλαίσια", "not applicable — coplanar modules")]
              : ["Row shading", SHADING[shading] || shading],
            ["Terrain horizon", horizonCount ? `${horizonCount} points measured` : "not measured"],
            ["Soiling", soilingMonthly
              ? tr(`ανά μήνα, ${fmt(Math.min(...soilingProfile.map(num)), 1)}–${fmt(Math.max(...soilingProfile.map(num)), 1, " %")}`,
                `by month, ${fmt(Math.min(...soilingProfile.map(num)), 1)}–${fmt(Math.max(...soilingProfile.map(num)), 1, " %")}`)
              : soiling === null ? "—" : fmt(soiling, 1, " %")],
            // A sentence, like Soiling above it. The curve is several
            // numbers now and the honest summary of a hand-edited table is
            // that it was edited by hand -- which no single box can say.
            ["Degradation", degradation],
          ]}
        />
        <Group
          title="Connection"
          owner="Grid Connection"
          rows={[
            ["Level", grid.connectionLevel ? `${grid.connectionLevel}${num(grid.voltageV) ? `, ${num(grid.voltageV) >= 1000 ? fmt(grid.voltageV / 1000, 0, " kV") : fmt0(grid.voltageV, " V")}` : ""}` : "—"],
            ["Export limit", exportKw === null ? "—" : `${fmt0(exportKw, " kW")}${grid.enforceLimits ? " (enforced 24/7)" : ""}`],
            ["Import limit", fmt0(grid.importLimitKw, " kW")],
            ["Transformer", transformerKva === undefined
              ? (cfg?.hasTransformer ? (cfg?.useRecommendedTransformer ? "sized by the run" : fmt0(cfg?.transformerDets?.rated, " kVA")) : "none")
              : (transformerKva || "none")],
            ["PV asset", String(cfg?.pvAssetStatus || "new") === "existing" ? "Existing plant (sunk asset)" : "New build (in CAPEX)"],
          ]}
        />
      </Box>

      {Array.isArray(bessRows) && bessRows.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Group
            title="Batteries"
            owner="BESS"
            rows={bessRows.map((r, i) => [
              r?.name || r?.battery_name || `Battery ${i + 1}`,
              `${fmt0(r?.count ?? 1)} × ${r?.powerMw != null ? power(r.powerMw * 1000) : "—"} / ${r?.energyMwh != null ? power(r.energyMwh * 1000, "Wh") : "—"}`,
            ])}
          />
        </>
      )}
    </Paper>
  );
}
